<template>
    <div id="production-init" class="hide-scrollbar">
        <fieldset class="forecast-configuration" :disabled="configurationLocked">
        <!--Para el dia actual-->
        <div v-if="step == SALES_CURRENT" class="channels">
            <div class="forecast-defrost">{{ $t('production.init.forecast_title') }}</div>
            <ForecastTopbar :isLoading="isLoading" :date="formatDate(channels_sales_current.reference)" :sales="forecastSales" />
            <ForecastSetupDate ref="forecastSetupDate" :date="channels_sales_current.reference" :sales-channel-config="channels_sales_current" :pmix-channel-config="channels_pmix_current" :isLoading="isLoading" :forecast="sales" :isAdjustable="isAdjustable" :oModifier="modifier" @updateSalesConfig="updateSalesConfig($event, 'current')" @updatePmixConfig="updatePmixConfig($event, 'current')" @updateModifier="updateModifier($event)" />
        </div>

        <!--Para el siguiente dia-->
        <div v-else class="channels">
            <div class="forecast-defrost">{{ $t('production.init.forecast_title_defrost') }}</div>
            <ForecastTopbar :isLoading="isLoading" :date="formatDate(channels_sales_defrost.reference)" :sales="forecastSales" />
            <ForecastSetupDate ref="forecastSetupDateDefrost" :isLoading="isLoading" :date="channels_sales_defrost.reference" :sales-channel-config="channels_sales_defrost" :pmix-channel-config="channels_pmix_defrost" :forecast="sales" :isAdjustable="isAdjustable" :oModifier="modifier" @updateSalesConfig="updateSalesConfig($event, 'defrost')" @updatePmixConfig="updatePmixConfig($event, 'defrost')" @updateModifier="updateModifier($event)" />
        </div>

        </fieldset>
        <div v-if="step == SALES_CURRENT && advancedSettings" class="advanced-summary">
            <div class="advanced-summary-title">{{ $t('production.advanced.applied_title') }}</div>
            <div class="advanced-summary-body">
                <div v-for="(period, index) in advancedSettings.periods" :key="index" class="advanced-summary-row">
                    <span class="advanced-tag time">◷ {{ advancedSettings.all_day ? $t('production.advanced.all_day_tag') : $t('production.advanced.interval_tag', { start: period.start, end: period.end }) }}</span>
                    <span class="advanced-tag adjustment">↗ {{ adjustmentLabel(period) }}</span>
                </div>
            </div>
        </div>

        <div class="btnExit">
            <template v-if="step == SALES_CURRENT">
                <button v-if="gcAdvanceForecastFlag" class="btn advanced-settings" :disabled="configurationLocked" @click="$emit('openAdvancedSettings')">{{ $t('production.advanced.button') }} <i class="i-andy-filter2" aria-hidden="true"></i></button>
                <button class="btn step next" :disabled="isSubmitting || !canGoToDefrostStep" :class="{ disabled: isSubmitting || !canGoToDefrostStep }" @click="changeStep(2)">{{ $t('production.select.next') }}</button>
            </template>
            <template v-else>
                <button class="btn step prev" :disabled="configurationLocked" @click="changeStep(1)">{{ $t('production.select.previous') }}</button>
                <button class="btn save-changes" :disabled="!canConfirmForecast" :class="{ disabled: !canConfirmForecast }" @click="confirmForecast()">{{ $t(confirmationLabel) }}</button>
            </template>
        </div>
    </div>
</template>

<script>
import ForecastTopbar from './forecastTopbar.vue'
import ForecastSetupDate from './forecastSetupDate.vue'
import { formatDate, isValidDate } from '@/utils/dateUtils'

const projectionPopupOwners = new WeakMap()

function initialChannelState(refDateStr) {
    const w = moment(refDateStr).locale('en').format('dddd').toLowerCase()
    return { general: w, delivery: w, reference: refDateStr }
}

export default {
    components: {
        ForecastTopbar,
        ForecastSetupDate
    },
    name: 'ProductionInit',
    props: {
        forecast: { type: Object },
        products: { type: Array, default: () => [] },
        contextKey: { type: String, default: '' },
        advancedSettings: { type: Object, default: null }
    },
    data() {
        const ref = (this.forecast && this.forecast.reference) ? this.forecast.reference : moment().format('YYYY-MM-DD')
        const defRef = moment(ref).add(1, 'days').format('YYYY-MM-DD')
        return {
            SALES_CURRENT: 1,
            SALES_DEFROST: 2,
            step: 1,
            isLoadingCurrent: false,
            isLoadingDefrost: false,
            isSubmitting: false,
            isDisposed: false,
            submission: null,
            activeSubmission: null,
            channels_sales_current: initialChannelState(ref),
            channels_sales_defrost: initialChannelState(defRef),
            channels_pmix_current: initialChannelState(ref),
            channels_pmix_defrost: initialChannelState(defRef),
            channel_types_sales_current: {'general': 'weekday', 'delivery': 'weekday'},
            channel_types_sales_defrost: {'general': 'weekday', 'delivery': 'weekday'},
            channel_types_pmix_current: {'general': 'weekday', 'delivery': 'weekday'},
            channel_types_pmix_defrost: {'general': 'weekday', 'delivery': 'weekday'},
            modifier: {'general': 0, 'delivery': 0},
            modifiers: {},
        }
    },
    computed: {
        sales() {
            let endpoint = this.step == this.SALES_CURRENT ? 'getSales' : 'getDefrostSales'
            let data = this.$store.getters['production/' + endpoint]
            if (!data) {
                return null
            }

            let modifier_general = parseFloat(1 + this.modifier.general / 100)
            let modifier_delivery = parseFloat(1 + this.modifier.delivery / 100)
            if(modifier_general < 0) {
                modifier_general = 0
            }
            if(modifier_delivery < 0) {
                modifier_delivery = 0
            }
            let data_raw = JSON.parse(JSON.stringify(data))

            data_raw.general.sales_forecast.total = modifier_general * data_raw.general.sales_forecast.total
            data_raw.delivery.sales_forecast.total = modifier_delivery * data_raw.delivery.sales_forecast.total
            for(let key in data.general.sales_forecast.sales_forecast) {
                data_raw.general.sales_forecast.sales_forecast[key] = modifier_general * data_raw.general.sales_forecast.sales_forecast[key]
            }
            for(let key in data.delivery.sales_forecast.sales_forecast) {
                data_raw.delivery.sales_forecast.sales_forecast[key] = modifier_delivery * data_raw.delivery.sales_forecast.sales_forecast[key]
            }

            data = {...data_raw}
            return data
        },
        forecastSales() {
            if(!this.sales) {
                return null
            }
            if(!this.sales.general || !this.sales.delivery) {
                return null
            }
            if(!this.sales.general.sales_forecast || !this.sales.delivery.sales_forecast) {
                return null
            }
            return this.sales.general.sales_forecast.total + this.sales.delivery.sales_forecast.total
        },
        date() {
            return this.forecast.reference ? this.forecast.reference : moment().format('YYYY-MM-DD')
        },
        isAdjustable() {
            return this.$store.getters['production/getOption']('SALES_FORECAST_TYPE') == 'MANUAL'
        },
        isLoading() {
            return this.step === this.SALES_CURRENT ? this.isLoadingCurrent : this.isLoadingDefrost
        },
        isCurrentChannelsValid() {
            return this.isChannelPairValid(this.channels_sales_current, this.channel_types_sales_current) &&
                this.isChannelPairValid(this.channels_pmix_current, this.channel_types_pmix_current)
        },
        isDefrostChannelsValid() {
            return this.isChannelPairValid(this.channels_sales_defrost, this.channel_types_sales_defrost) &&
                this.isChannelPairValid(this.channels_pmix_defrost, this.channel_types_pmix_defrost)
        },
        canGoToDefrostStep() {
            return this.isCurrentChannelsValid
        },
        canConfirmForecast() {
            return !this.isDisposed && !this.isSubmitting && ((this.submission && this.submission.confirmed) ||
                (this.isCurrentChannelsValid && this.isDefrostChannelsValid))
        },
        configurationLocked() {
            return this.isSubmitting || Boolean(this.submission && this.submission.confirmed)
        },
        confirmationLabel() {
            if (this.submission && this.submission.confirmed) {
                return this.submission.advancedSaved
                    ? 'production.select.retry_load_forecast'
                    : 'production.select.retry_advanced_settings'
            }
            return 'production.select.confirm'
        },
        gcAdvanceForecastFlag() {
            return this.$store.getters['production/getGcAdvanceForecastFlag']
        }
    },
    methods: {
        load() {
            let self = this

            var params = {
                dateref: this.channels_sales_current.reference,
                date1: this.channels_sales_current.general,
                date2: this.channels_sales_current.delivery
            }
            if (this.step == this.SALES_CURRENT) {
                this.isLoadingCurrent = true
                this.$store.dispatch('production/loadSales', params).then(function () {
                    self.isLoadingCurrent = false
                })
                this.modifier = this.getModifier(this.channels_sales_current.reference)
            }
            var defrostParams = {
                dateref: this.channels_sales_defrost.reference,
                date1: this.channels_sales_defrost.general,
                date2: this.channels_sales_defrost.delivery
            }
            if (this.step == this.SALES_DEFROST) {
                this.isLoadingDefrost = true
                this.$store.dispatch('production/loadDefrostSales', defrostParams).then(function () {
                    self.isLoadingDefrost = false
                })
                this.modifier = this.getModifier(this.channels_sales_defrost.reference)
            }
        },
        updateSalesConfig(channels, type) {
            if (this.configurationLocked) {
                return
            }
            if (type === 'current') {
                this.channels_sales_current.general = channels.general.value
                this.channels_sales_current.delivery = channels.delivery.value
                this.channel_types_sales_current.general = channels.general.type
                this.channel_types_sales_current.delivery = channels.delivery.type
            } else if (type === 'defrost') {
                this.channels_sales_defrost.general = channels.general.value
                this.channels_sales_defrost.delivery = channels.delivery.value
                this.channel_types_sales_defrost.general = channels.general.type
                this.channel_types_sales_defrost.delivery = channels.delivery.type
            }
            this.load()
        },
        updatePmixConfig(channels, type) {
            if (this.configurationLocked) {
                return
            }
            if (type === 'current') {
                this.channels_pmix_current.general = channels.general.value
                this.channels_pmix_current.delivery = channels.delivery.value
                this.channel_types_pmix_current.general = channels.general.type
                this.channel_types_pmix_current.delivery = channels.delivery.type
            } else if (type === 'defrost') {
                this.channels_pmix_defrost.general = channels.general.value
                this.channels_pmix_defrost.delivery = channels.delivery.value
                this.channel_types_pmix_defrost.general = channels.general.type
                this.channel_types_pmix_defrost.delivery = channels.delivery.type
            }
        },
        isChannelPairValid(channels, types) {
            return this.isChannelConfigValid(channels.general, types.general) &&
                this.isChannelConfigValid(channels.delivery, types.delivery)
        },
        isChannelConfigValid(channelValue, channelType) {
            if (channelType === 'weekday') {
                return true
            }
            if (channelType === 'custom') {
                return isValidDate(channelValue)
            }
            return false
        },
        confirmForecast() {
            if (!this.canConfirmForecast) {
                return
            }
            if (this.submission && this.submission.confirmed) {
                return this.submitForecast()
            }
            var self = this

            this.$popup.confirm({
                message: self.$t('production.select.popup_message'),
                textSave: self.$t('production.select.confirm'),
                textCancel: self.$t('config.printer.cancel'),
                callSave: () => self.submitForecast(),
                callCancel: function () {
                    self.$popup.close()
                }
            })
        },
        async submitForecast() {
            if (!this.canConfirmForecast) {
                return
            }
            this.isSubmitting = true
            if (!this.submission) {
                this.submission = {
                    date: this.date,
                    contextKey: this.contextKey,
                    confirmed: false,
                    advancedSaved: !this.advancedSettings,
                    advancedSettings: this.advancedSettings ? JSON.parse(JSON.stringify(this.advancedSettings)) : null,
                    params: {
                        general: this.channels_pmix_current.general,
                        delivery: this.channels_pmix_current.delivery,
                        general_defrost: this.channels_pmix_defrost.general,
                        delivery_defrost: this.channels_pmix_defrost.delivery,
                        general_sales: this.channels_sales_current.general,
                        delivery_sales: this.channels_sales_current.delivery,
                        general_sales_defrost: this.channels_sales_defrost.general,
                        delivery_sales_defrost: this.channels_sales_defrost.delivery,
                        modifiers: JSON.stringify(this.modifiers)
                    }
                }
            }
            const submission = this.submission
            this.activeSubmission = submission
            this.$emit('projectionPending', submission.date)
            this.$emit('projectionSaving', true)
            this.$popup.close()
            try {
                await new Promise(resolve => setTimeout(resolve, 300))
                if (!this.isCurrentSubmission(submission)) {
                    return
                }
                projectionPopupOwners.set(this.$popup, submission)
                this.$popup.projection({ closable: false })

                // Confirmar recalcula la base y elimina ajustes anteriores: debe hacerse primero.
                if (!submission.confirmed) {
                    const confirmation = await this.$store.dispatch('production/confirm', {
                        ref: submission.date,
                        params: submission.params
                    })
                    if (!this.isCurrentSubmission(submission)) {
                        return
                    }
                    if (!confirmation || !confirmation.status) {
                        throw new Error(confirmation && confirmation.message ? confirmation.message : 'Could not confirm forecast')
                    }
                    submission.confirmed = true
                }
                if (!submission.advancedSaved) {
                    const advanced = await this.$store.dispatch('production/saveAdvancedSettings', {
                        date: submission.date,
                        settings: submission.advancedSettings
                    })
                    if (!this.isCurrentSubmission(submission)) {
                        return
                    }
                    if (!advanced || !advanced.status) {
                        throw new Error(advanced && advanced.message ? advanced.message : 'Could not save advanced settings')
                    }
                    submission.advancedSaved = true
                }

                const forecast = await this.$store.dispatch('production/loadForecast', submission.date)
                if (!this.isCurrentSubmission(submission)) {
                    return
                }
                if (!forecast || !forecast.status) {
                    throw new Error(forecast && forecast.message ? forecast.message : 'Could not load forecast')
                }
                this.$emit('projectionConfirmed')
            } catch (error) {
                if (!this.isCurrentSubmission(submission)) {
                    return
                }
                logError(error)
                let message = 'production.select.confirm_error'
                if (submission.confirmed) {
                    message = submission.advancedSaved
                        ? 'production.select.forecast_load_error'
                        : 'production.select.advanced_save_error'
                } else {
                    this.submission = null
                    this.$emit('projectionPending', null)
                }
                this.closeProjectionPopup(submission)
                this.$popup.alert({ message: this.$t(message) })
                return
            } finally {
                this.closeProjectionPopup(submission)
                if (this.activeSubmission === submission) {
                    this.activeSubmission = null
                    this.isSubmitting = false
                    if (!this.isDisposed && this.contextKey === submission.contextKey && this.date === submission.date) {
                        this.$emit('projectionSaving', false)
                    }
                }
            }
        },
        closeProjectionPopup(submission) {
            if (submission && projectionPopupOwners.get(this.$popup) === submission) {
                projectionPopupOwners.delete(this.$popup)
                this.$popup.close()
            }
        },
        invalidateSubmission() {
            const active = this.activeSubmission
            this.closeProjectionPopup(active)
            this.activeSubmission = null
            this.submission = null
            this.isSubmitting = false
            if (active && !this.isDisposed) {
                this.$emit('projectionSaving', false)
            }
        },
        isCurrentSubmission(submission) {
            return !this.isDisposed && this.submission === submission &&
                this.contextKey === submission.contextKey && this.date === submission.date
        },
        formatDate,
        formatWeekday(date) {
            return moment(date).locale('en').format('dddd').toLowerCase()
        },
        changeStep(step) {
            if (this.configurationLocked) {
                return
            }
            if (step === this.SALES_DEFROST && !this.canGoToDefrostStep) {
                return
            }
            if (this.step === step) {
                return
            }
            this.step = step
            this.load()
        },
        updateDates(date) {
            this.invalidateSubmission()
            this.$emit('projectionPending', null)
            const general = this.formatWeekday(date)
            const defRef = moment(date).add(1, 'days').format('YYYY-MM-DD')
            const defGeneral = this.formatWeekday(defRef)
            this.channels_sales_current = { reference: date, general, delivery: general }
            this.channels_pmix_current = { reference: date, general, delivery: general }
            this.channels_sales_defrost = { reference: defRef, general: defGeneral, delivery: defGeneral }
            this.channels_pmix_defrost = { reference: defRef, general: defGeneral, delivery: defGeneral }
            this.channel_types_sales_current = { general: 'weekday', delivery: 'weekday' }
            this.channel_types_sales_defrost = { general: 'weekday', delivery: 'weekday' }
            this.channel_types_pmix_current = { general: 'weekday', delivery: 'weekday' }
            this.channel_types_pmix_defrost = { general: 'weekday', delivery: 'weekday' }
        },
        updateModifier(data) {
            if (this.configurationLocked) {
                return
            }
            this.modifier = data.modifier
            this.modifiers[data.date] = data.modifier
        },
        getModifier(date) {
            return this.modifiers[date] || {'general': 0, 'delivery': 0}
        },
        adjustmentLabel(period) {
            const percentage = `${Number(period.percentage) > 0 ? '+' : ''}${period.percentage}%`
            if (this.advancedSettings.type === 'sales') {
                return this.$t('production.advanced.sales_tag', { percentage })
            }
            const names = this.advancedSettings.all_products
                ? this.$t('production.advanced.all_products')
                : this.products.filter(product => this.advancedSettings.products.includes(product.id)).map(product => product.name).join(', ')
            return this.$t('production.advanced.pmix_tag', { percentage, products: names })
        }
    },
    beforeDestroy() {
        this.isDisposed = true
        this.invalidateSubmission()
    },
    watch: {
        contextKey() {
            this.invalidateSubmission()
            this.$emit('projectionPending', null)
        },
        date: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.updateDates(newVal)
                    this.step = this.SALES_CURRENT
                    this.load()
                }
            },
            immediate: true
        }
    }
}
</script>
<style lang="scss" scoped>
#production-init {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding-top: 0;
    overflow: hidden;
    overflow-y: auto;
    white-space: nowrap;

    .forecast-configuration {
        min-width: 0;
        margin: 0;
        padding: 0;
        border: 0;

        &:disabled {
            pointer-events: none;
        }
    }

    .title {
        font-family: $text-bold;
        @include font-size(l);
        margin-bottom: 64px;
        text-align: center;
        margin: 0.8rem 0 1rem 0;
    }
    .channels {
        display: flex;
        flex-direction: column;
        width: 100%;
        position: relative;
        justify-content: space-around;
        gap: 16px;

        &.loaded {
            // background-color: $color-primary-100;
            // margin-bottom: 0.7em;
            // height: 100%;
        }
        &.loading {
            &:before {
                content: ' ';
                @include background($image: img('loader_light.svg'), $size: 60px, $position: center center);
                background-color: rgba(0, 0, 0, 0.5);
                width: 100%;
                height: 100%;
                z-index: 10;
                position: absolute;
            }
        }

        .forecast-defrost {
            @include font-size(l);
            width: 100%;
            text-align: center;
            font-family: $text;
            padding-top: 8px;
            font-family: $text-bold;
        }
    }

    .btnExit {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: 10px;
        height: 10%;
        margin-top: 8px;
        .btn {
            padding: 0px;
            height: 45px;
            width: 200px;
            margin: 0;
            &.save-changes {

            }
            &.cancel,
            &.prev {
                background-color: $color-neutral-400;
                font-family: $text;
                color: $color-black;
            }
            &.advanced-settings {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                gap: 12px;
                width: auto;
                min-width: 200px;
                height: 45px;
                min-height: 45px;
                padding: 0 15px;
                border: 1px solid $color-neutral-500;
                border-radius: 4px;
                background-color: $color-white;
                color: $color-black;
                font-family: $text-bold;
                font-size: 18px;
                line-height: 1;
                white-space: nowrap;

                i {
                    font-size: 18px;
                }
            }
        }
    }

    .advanced-summary {
        margin: 10px 0 4px;
        padding: 8px;
        background: #fff;
        white-space: normal;
    }
    .advanced-summary-title { padding: 8px 10px; background: #f6f6f6; color: #333; font-family: $text-bold; font-size: 14px; }
    .advanced-summary-body { padding: 8px 10px 2px; }
    .advanced-summary-row { display: flex; flex-wrap: wrap; gap: 6px; }
    .advanced-tag { padding: 5px 8px; border: 1px solid #e4e8f0; border-radius: 3px; background: #fff; color: #555; font-size: 12px; line-height: 1.4; }
    .advanced-tag.adjustment { color: #374673; }

    .loading {
        @include background($image: img('loader_dark.svg'), $size: 100px, $position: center center);
        width: 100%;
        height: 100%;
        position: absolute;
    }
}
</style>
